import { Box, HStack, Image, Text } from '@chakra-ui/react';
// import { onElementVisible } from '@pocapan/shared/utils/UIUtils';
import { t } from 'i18next';
import { filter, find, first, isEmpty, isUndefined } from 'lodash';
import { HomeFill, HomeLine, Map as MapIcon, Plus, Pocabinder } from '@peacecover/icon';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getArtistId, getUserId, sleep } from '@pocapan/shared/utils/etcUtils';
import saveData from '@pocapan/shared/utils/saveData';
import { useArtistQueryAll } from '../api/queries/useArtistQuery';

import GridSkeletons from '../skeletons/GridSkeletons';

const ArtistIconSelector = ({
  onSelected,
  onAddCallbackUrl,
  enableAddCard = true,
  showHomeOption = false,
  showBinderCard = false,
  showOffPanOption = false
}) => {
  const ref = useRef();
  const navigate = useNavigate();
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [homeSelected, setHomeSelected] = useState(false);
  const artists = useArtistQueryAll();

  // #region 컴포넌트가 화면에 보여졌을때를 감지해서 선택된 엘리먼트로 스크롤링한다.
  const selectedArtistToCenter = (artistId) => {
    const selectedElement = document.getElementById(`artistIcon_${artistId}`);
    if (selectedElement) {
      selectedElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  };

  // useEffect(() => {
  //   return onElementVisible(ref, () => {
  //     async function scroll() {
  //       await sleep(100);
  //       selectedArtistToCenter(getArtistId());
  //     }
  //     scroll();

  //     if (selectedArtist) {
  //       onSelected?.(selectedArtist);
  //     } else {
  //       selectInitialArtist();
  //     }
  //   });
  // }, [selectedArtist]);
  // #endregion

  useEffect(() => {
    if (isEmpty(artists)) return; // 아티스트 리스트가 없으면 Early return
    if (!isEmpty(selectedArtist)) return; // 선택된 아티스트가 있으면 Early return

    let initArtist = first(artists);
    const savedArtistId = getArtistId();
    if (savedArtistId) {
      // 저장된 아티스트가 있는 경우
      initArtist = find(artists, (item) => !!item.likedDate && item.artistId === savedArtistId);
    }

    setSelectedArtist(initArtist);
    onSelected?.(initArtist);
    async function scroll() {
      await sleep(500);
      selectedArtistToCenter(getArtistId());
    }
    scroll();
  }, [onSelected, artists, selectedArtist]);

  const handleHomeSelect = () => {
    setHomeSelected(true);
    onSelected?.(null);
  };

  const handleArtistSelect = (artist) => {
    saveData('artist', artist.artistId);
    setSelectedArtist(artist);
    setHomeSelected(false);
    onSelected?.(artist);
    selectedArtistToCenter(artist.artistId);
  };

  return (
    <Box ref={ref}>
      <HStack
        spacing={'9px'}
        px={'20px'}
        overflowY={'hidden'}
        css={{ '::-webkit-scrollbar': { display: 'none' } }}
      >
        {showHomeOption && <HomeCard selected={homeSelected} onSelect={handleHomeSelect} />}
        {showBinderCard && (
          <PocaBinderCard onSelect={() => navigate(`/users/${getUserId()}/binder`)} />
        )}
        {/* 여기 진입 경로 추가 */}
        {showOffPanOption && <OffPanCard onSelect={() => navigate('/offline-locations')} />}
        {
          // TODO : Refactor with ListRenderer
          isUndefined(artists) ? (
            <GridSkeletons
              gridSize={1}
              skeletonSize={1}
              skeletonHeight={'96px'}
              marginBottom={'16px'}
              width={'100%'}
            />
          ) : isEmpty(artists) ? (
            <></>
          ) : (
            artists
              .filter((artist) => !!artist.likedDate)
              .map((artist) => (
                <ArtistCard
                  key={artist.artistId}
                  artist={artist}
                  selected={!homeSelected && artist.artistId === selectedArtist?.artistId}
                  onSelect={(_) => handleArtistSelect(artist)}
                />
              ))
          )
        }
        {!isUndefined(artists) &&
          artists &&
          filter(artists, (a) => !!a.likedDate).length < artists.length &&
          enableAddCard && <AddCard onAddCallbackUrl={onAddCallbackUrl} />}
        <div
          style={{
            width: '20px' /* 또는 원하는 공간의 크기 */,
            flexShrink: '0' /* Flex 컨테이너 내에서 이 요소가 축소되지 않도록 함 */
          }}
        />
      </HStack>
    </Box>
  );
};

const HomeCard = ({ selected, onSelect }) => {
  return (
    <Box
      as="button"
      onClick={onSelect}
      style={{ position: 'relative' }}
      width="64px"
      height="108px"
    >
      {!selected && (
        <div
          style={{
            position: 'absolute',
            top: '4px',
            left: '4px',
            width: '58px',
            height: '58px',
            borderRadius: '50%',
            boxShadow: '0 0 1px 0 rgba(0, 0, 0, 0.20) inset',
            zIndex: 1,
            pointerEvents: 'none'
          }}
        />
      )}
      <div
        className={selected ? 'no-animation' : 'fade-out'}
        style={{ position: 'absolute', top: '4px', left: '4px', zIndex: 1 }}
      >
        <svg width="58" height="58" aria-hidden="true">
          <circle
            className="stroke-onSecondary-light dark:stroke-onSecondary-dark"
            cx="29"
            cy="29"
            r="28"
            fill="none"
            strokeWidth="1.5"
          />
        </svg>
      </div>
      <Box width="64px" height="108px" mx={'7px'} mt={'7px'}>
        <Box
          style={{
            width: '52px',
            height: '52px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px'
          }}
        >
          {selected ? (
            <HomeFill size={36} className="text-primary-light dark:text-primary-dark" />
          ) : (
            <HomeLine size={36} className="text-text-main-lv2" />
          )}
        </Box>
        <Text
          fontSize="14px"
          py={'14px'}
          className={`text-center ${selected ? 'text-onSecondary-light dark:text-onSecondary-dark' : 'text-main-light-2 dark:text-main-dark-2'}`}
          fontWeight={600}
          lineHeight={'17px'}
          noOfLines={2}
          whiteSpace="pre-line"
          marginLeft={'-12px'}
        >
          {t('tabs.home')}
        </Text>
      </Box>
    </Box>
  );
};

const PocaBinderCard = ({ onSelect }) => {
  return (
    <Box
      as="button"
      onClick={onSelect}
      style={{ position: 'relative' }}
      width="64px"
      height="108px"
    >
      <div
        style={{
          position: 'absolute',
          top: '4px',
          left: '4px',
          width: '58px',
          height: '58px',
          borderRadius: '50%',
          boxShadow: '0 0 1px 0 rgba(0, 0, 0, 0.20) inset',
          zIndex: 1,
          pointerEvents: 'none'
        }}
      />
      <Box width="64px" height="108px" mx={'7px'} mt={'7px'}>
        <Box
          style={{
            width: '52px',
            height: '52px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px'
          }}
        >
          <Pocabinder size={36} className="text-text-main-lv2" />
        </Box>
        <Text
          fontSize="14px"
          py={'14px'}
          className="text-center text-main-light-2 dark:text-main-dark-2"
          fontWeight={600}
          lineHeight={'17px'}
          noOfLines={2}
          whiteSpace="pre-line"
          marginLeft={'-12px'}
        >
          {t('tabs.pocaBinder')}
        </Text>
      </Box>
    </Box>
  );
};

const OffPanCard = ({ onSelect }) => {
  return (
    <Box
      as="button"
      onClick={onSelect}
      style={{ position: 'relative' }}
      width="64px"
      height="108px"
    >
      <div
        style={{
          position: 'absolute',
          top: '4px',
          left: '4px',
          width: '58px',
          height: '58px',
          borderRadius: '50%',
          boxShadow: '0 0 1px 0 rgba(0, 0, 0, 0.20) inset',
          zIndex: 1,
          pointerEvents: 'none'
        }}
      />
      <Box width="64px" height="108px" mx={'7px'} mt={'7px'}>
        <Box
          style={{
            width: '52px',
            height: '52px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px'
          }}
        >
          <MapIcon size={36} className="text-text-main-lv2" />
        </Box>
        <Text
          fontSize="14px"
          py={'14px'}
          className="text-center text-main-light-2 dark:text-main-dark-2"
          fontWeight={600}
          lineHeight={'17px'}
          noOfLines={2}
          whiteSpace="pre-line"
          marginLeft={'-12px'}
        >
          {t('tabs.offPan')}
        </Text>
      </Box>
    </Box>
  );
};

const ArtistCard = ({ artist, selected, onSelect }) => {
  const [applyAnimation, setApplyAnimation] = useState(false);
  const getClassName = () => {
    if (!applyAnimation) return selected ? 'no-animation' : 'fade-out';
    return selected ? 'fade-in' : 'fade-out';
  };
  const onClick = (e) => {
    if (!applyAnimation) setApplyAnimation(true);
    onSelect?.(e);
  };
  return (
    <Box
      as="button"
      onClick={onClick}
      style={{ position: 'relative' }}
      width="64px"
      height="108px"
      id={`artistIcon_${artist.artistId}`}
    >
      {!selected && (
        <div
          style={{
            position: 'absolute',
            top: '4px',
            left: '4px',
            width: '58px',
            height: '58px',
            borderRadius: '50%',
            boxShadow: '0 0 1px 0 rgba(0, 0, 0, 0.20) inset',
            zIndex: 1,
            pointerEvents: 'none'
          }}
        />
      )}
      <div
        className={getClassName()}
        style={{ position: 'absolute', top: '4px', left: '4px', zIndex: 1 }}
      >
        <svg width="58" height="58" aria-hidden="true">
          <circle
            className="stroke-onSecondary-light dark:stroke-onSecondary-dark"
            cx="29"
            cy="29"
            r="28"
            fill="none"
            strokeWidth="1.5"
          />
        </svg>
      </div>

      <Box width="64px" height="108px" mx={'7px'} mt={'7px'}>
        <Image
          className="border-[1px] border-placeholder-light dark:border-placeholder-dark"
          style={{
            width: '52px',
            height: '52px',
            borderRadius: '50%',
            objectFit: 'cover'
          }}
          src={artist.thumbnailUrl}
        />
        <Text
          fontSize="14px"
          py={'14px'}
          className={`text-center ${
            selected
              ? 'text-onSecondary-light dark:text-onSecondary-dark'
              : 'text-main-light-2 dark:text-main-dark-2'
          }`} // FIXME : textColor={selected ? "#5C3DFA" : "#4E4E4E"}
          fontWeight={600}
          lineHeight={'17px'}
          noOfLines={2}
          whiteSpace="pre-line"
          marginLeft={'-12px'}
        >
          {artist.shortName || artist.name}
        </Text>
      </Box>
    </Box>
  );
};

const AddCard = ({ onAddCallbackUrl }) => {
  const navigate = useNavigate();
  return (
    <Box
      as="button"
      onClick={(_) => {
        if (onAddCallbackUrl) {
          navigate(`/search-artists?notInitial=true&callback=${onAddCallbackUrl}`);
        } else {
          navigate('/search-artists?notInitial=true');
        }
      }}
      width="64px"
      height="115px"
      style={{
        position: 'relative'
      }}
    >
      <div style={{ position: 'absolute', top: '8px', left: '4px', zIndex: 1 }}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="58"
          height="58"
          viewBox="0 0 58 58"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="29" cy="29" r="29" className="fill-black/[0.04] dark:fill-white/[0.06]" />
        </svg>
      </div>

      <div style={{ position: 'absolute', top: '25px', left: '21px', zIndex: 2 }}>
        <Plus size={24} className="text-text-sub-lv3" />
      </div>
      <Box px={'6px'}>
        <Box width={'64px'} height={'65px'} />
        <Text
          className="text-main-light-2 dark:text-main-light-2" // FIXME textColor={"#4E4E4E"}
          fontSize="14px"
          pb={'10px'}
          fontWeight={600}
          lineHeight={'17px'}
          marginLeft={'2px'}
        >
          {t('manageArtistPage.addLikedArtist')}
        </Text>
      </Box>
    </Box>
  );
};

export default ArtistIconSelector;
